import { Dimensions, PixelRatio, Platform, useWindowDimensions } from 'react-native';
import { useMemo } from 'react';

const calcWidth = () => {
  // return Math.min(Dimensions.get('window').width, maxAppWidth);

  return Platform.OS === 'web' && global.window ? window.innerWidth : Dimensions.get('window').width;
};

const calcHeight = () => {
  // return Math.min(Dimensions.get('window').height, maxAppHeight) - (Platform.OS !== 'web' ? 0 : 0); // StatusBar.currentHeight
  return Platform.OS === 'web' && global.window ? window.innerHeight : Dimensions.get('window').height;
};

// const maxAppWidth = Platform.OS === 'web' ? 700 : Number.MAX_SAFE_INTEGER;
// const maxAppHeight = Platform.OS === 'web' ? 1200 : Number.MAX_SAFE_INTEGER;
let screenWidth = calcWidth();
let screenHeight = calcHeight();

let unsubscribeDimensions: (() => void) | null = null;

if (Platform.OS === 'web') {
  const subscription = Dimensions.addEventListener('change', () => {
    screenWidth = calcWidth();
    screenHeight = calcHeight();
  });
  unsubscribeDimensions = () => subscription.remove();
}

if ((module as any).hot) {
  (module as any).hot.dispose(() => {
    unsubscribeDimensions?.();
  });
}

export function useScreenSize() {
  const dimensions = useWindowDimensions();
  const width = useMemo(() => screenWidth, [dimensions]);
  const height = useMemo(() => screenHeight, [dimensions]);
  return useMemo(() => ({ width, height, wp, hp, min }), [width, height]);
}

function min(percent: number) {
  // Parse string percentage input and convert it to number.

  // Use PixelRatio.roundToNearestPixel method in order to round the layout
  // size (dp) to the nearest one that corresponds to an integer number of pixels.
  return PixelRatio.roundToNearestPixel(((screenWidth < screenHeight ? screenWidth : screenHeight) * percent) / 100);
}

/**
 * Converts provided width percentage to independent pixel (dp).
 * @param  {string} widthPercent The percentage of screen's width that UI element should cover
 *                               along with the percentage symbol (%).
 * @return {number}              The calculated dp depending on current device's screen width.
 */
function wp(widthPercent: number | string) {
  // Parse string percentage input and convert it to number.
  const elemWidth = typeof widthPercent === 'number' ? widthPercent : parseFloat(widthPercent);

  // Use PixelRatio.roundToNearestPixel method in order to round the layout
  // size (dp) to the nearest one that correspons to an integer number of pixels.
  return PixelRatio.roundToNearestPixel((screenWidth * elemWidth) / 100);
}

/**
 * Converts provided height percentage to independent pixel (dp).
 * @param  {string} heightPercent The percentage of screen's height that UI element should cover
 *                                along with the percentage symbol (%).
 * @return {number}               The calculated dp depending on current device's screen height.
 */
function hp(heightPercent: number | string) {
  // Parse string percentage input and convert it to number.
  const elemHeight = typeof heightPercent === 'number' ? heightPercent : parseFloat(heightPercent);

  // Use PixelRatio.roundToNearestPixel method in order to round the layout
  // size (dp) to the nearest one that corresponds to an integer number of pixels.
  return PixelRatio.roundToNearestPixel((screenHeight * elemHeight) / 100);
}

global.hp = hp;
global.wp = wp;
global.max = (value: number) => (screenWidth > screenHeight ? wp(value) : hp(value));
global.min = (value: number) => (screenWidth > screenHeight ? hp(value) : wp(value));

const BASE_WIDTH = 412; // The standard width your designer used (Galaxy A32)

export function normalize(size: number, factor: number = 0.5) {
  const dimension = Math.min(screenWidth, screenHeight);
  const scale = dimension / BASE_WIDTH;

  // This calculates the scaled size with your moderate factor
  const newSize = size + (size * scale - size) * factor;

  if (Platform.OS === 'web' || dimension > 700) {
    // Allow it to shrink, but cap the growth at 1.2x (20%) for desktop/tablets
    const clampedSize = Math.min(newSize, size * 1.2);
    return PixelRatio.roundToNearestPixel(clampedSize);
  }

  // Round for crisp mobile rendering
  return Math.round(PixelRatio.roundToNearestPixel(newSize));
}

// 🚀 Added 'factor' here so you retain full control when styling!
export function fontSize(size: number, factor: number = 0.5) {
  return normalize(size, factor);
}
